import { supabase } from '../lib/supabase';
import { calculateXpReward, awardXp, applyInactivityDecayIfNeeded, getInactivityWarning } from './rewardService';
import pushNotificationService from './pushNotificationService';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const computeStreak = (lastPracticeDate, previousStreak = 0) => {
  const todayStr = getTodayDate();
  if (!lastPracticeDate) return 1;
  const lastDate = new Date(lastPracticeDate);
  const todayDate = new Date(todayStr);
  const diff = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));
  if (diff === 0) return previousStreak || 1;
  if (diff === 1) return (previousStreak || 0) + 1;
  return 1;
};

export const profileService = {
  async getUserProfile() {
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', session?.user?.id)
        ?.maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // Apply inactivity decay before returning profile
      try {
        await applyInactivityDecayIfNeeded(session.user.id);
      } catch (decErr) {
        console.warn('Inactivity decay check skipped:', decErr?.message || decErr);
      }

      // Refetch key fields after potential decay
      const { data: refreshed } = await supabase
        ?.from('user_profiles')
        ?.select('xp_points, level, last_practice_date, last_decay_date, streak_days')
        ?.eq('id', session?.user?.id)
        ?.maybeSingle();

      const inactivityWarning = await getInactivityWarning(session.user.id);

      // Optional: schedule push reminder if decay is imminent (within 1 day)
      try {
        if (inactivityWarning && inactivityWarning.daysUntilDecay <= 1) {
          const permOk = await pushNotificationService.requestPermission();
          if (permOk) {
            await pushNotificationService.updateNotificationSchedule('decay_warning', {
              notification_time: '09:00',
              enabled: true,
              channel: 'push'
            });
            await pushNotificationService.updateNotificationSchedule('decay_warning_email', {
              notification_time: '09:05',
              enabled: true,
              channel: 'email'
            });
          }
        }
      } catch (notifErr) {
        console.warn('Decay reminder scheduling skipped:', notifErr?.message || notifErr);
      }

      // Optional: immediate email reminder via Edge Function when decay is imminent
      try {
        if (inactivityWarning && inactivityWarning.daysUntilDecay <= 1 && data?.email) {
          await supabase.functions.invoke('decay_warning_email', {
            body: {
              toEmail: data.email,
              fullName: data.full_name,
              xpAtRisk: inactivityWarning.xpAtRisk,
              daysUntilDecay: inactivityWarning.daysUntilDecay,
              nextDecayDate: inactivityWarning.nextDecayDate
            }
          });
        }
      } catch (funcErr) {
        console.warn('Immediate decay email invocation skipped:', funcErr?.message || funcErr);
      }

      return {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        avatarUrl: data?.avatar_url,
        companyName: data?.company_name,
        industry: data?.industry,
        role: data?.role,
        onboardingCompleted: data?.onboarding_completed,
        xpPoints: refreshed?.xp_points ?? data?.xp_points,
        level: refreshed?.level ?? data?.level,
        streakDays: refreshed?.streak_days ?? data?.streak_days,
        lastPracticeDate: refreshed?.last_practice_date ?? data?.last_practice_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        inactivityWarning
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  async updateProfileAfterDrill(drillScore = 0, difficultyData = {}) {
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('Not authenticated');

      const currentProfile = await this.getUserProfile();
      if (!currentProfile) throw new Error('Profile not found');

      const reward = calculateXpReward(
        drillScore || 0,
        'dailyDrill',
        currentProfile?.skill_level || 'beginner',
        { ...difficultyData, currentLevel: currentProfile?.level || 1 }
      );
      const xpToAward = reward?.xp || 0;

      const xpResult = await awardXp(session.user.id, xpToAward, 'daily_drill');

      const today = getTodayDate();
      const newStreakDays = computeStreak(currentProfile?.lastPracticeDate, currentProfile?.streakDays || 0);

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          streak_days: newStreakDays,
          last_practice_date: today
        })
        ?.eq('id', session?.user?.id)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        avatarUrl: data?.avatar_url,
        companyName: data?.company_name,
        industry: data?.industry,
        role: data?.role,
        onboardingCompleted: data?.onboarding_completed,
        xpPoints: xpResult?.newXp ?? data?.xp_points,
        level: xpResult?.newLevel ?? data?.level,
        streakDays: data?.streak_days,
        lastPracticeDate: data?.last_practice_date,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        baseXP: xpToAward,
        bonusXP: reward?.bonus || 0,
        xpEarned: xpToAward,
        leveledUp: xpResult?.leveledUp
      };
    } catch (error) {
      console.error('Error updating profile after drill:', error);
      throw error;
    }
  }
};
